import { reactive } from 'vue'

/**
 * 工作台 ↔ 全局悬浮条 的桥接层。
 *
 * 原则：工作台是唯一状态源与行为源；这里只保存**只读投影**（未读数、来电/通话阶段、电话渠道是否可用），
 * 悬浮条按钮通过 `register` 进来的既有工作台函数执行，不直接操作 SDK，也不复制业务逻辑。
 * 工作台在签入后注册、签出/卸载时注销；未注册时悬浮条只显示返回入口。
 */
export type WorkbenchCallPhase = 'idle' | 'ringing' | 'calling' | 'talking'

export interface WorkbenchCallState {
  phase: WorkbenchCallPhase
  /** 来电号码 / 归属地展示文案 */
  phone: string
  /** 通话开始时间（ms），仅展示计时用 */
  startedAt: number | null
  /** TCCC 通话 sessionId */
  callId: string
}

export interface WorkbenchBridgeActions {
  /** 接听来电：绑定工作台 onPopupAccept（内部负责关弹屏、刷新列表） */
  answer: () => Promise<void> | void
  /** 拒接来电：绑定工作台 onPopupReject */
  reject: () => Promise<void> | void
  /** 挂断通话：绑定工作台 endCurrentService('call') */
  hangup: () => Promise<boolean> | void
  /** 打开某个会话：绑定工作台 onSelectSession */
  openSession: (sessionId: string) => Promise<void> | void
}

interface WorkbenchBridgeState {
  /** 工作台是否已注册（在线且实例常驻） */
  ready: boolean
  unreadTotal: number
  unreadSessionIds: string[]
  phoneAllowed: boolean
  call: WorkbenchCallState
  /** 红点点击后希望工作台打开的会话 */
  pendingSessionId: string
}

const state = reactive<WorkbenchBridgeState>({
  ready: false,
  unreadTotal: 0,
  unreadSessionIds: [],
  phoneAllowed: false,
  call: { phase: 'idle', phone: '', startedAt: null, callId: '' },
  pendingSessionId: ''
})

let actions: WorkbenchBridgeActions | null = null

const resetCall = () => {
  state.call = { phase: 'idle', phone: '', startedAt: null, callId: '' }
}

export const workbenchBridge = {
  state,
  get actions() {
    return actions
  },
  register(next: WorkbenchBridgeActions) {
    actions = next
    state.ready = true
  },
  unregister() {
    actions = null
    state.ready = false
    state.unreadTotal = 0
    state.unreadSessionIds = []
    state.phoneAllowed = false
    state.pendingSessionId = ''
    resetCall()
  },
  setUnread(total: number, sessionIds: string[]) {
    if (state.unreadTotal !== total) state.unreadTotal = total
    if (state.unreadSessionIds.join('\u0001') !== sessionIds.join('\u0001')) {
      state.unreadSessionIds = sessionIds
    }
  },
  setPhoneAllowed(allowed: boolean) {
    if (state.phoneAllowed !== allowed) state.phoneAllowed = allowed
  },
  setCall(next: Partial<WorkbenchCallState>) {
    state.call = { ...state.call, ...next }
  },
  resetCall,
  requestOpenSession(sessionId: string) {
    state.pendingSessionId = sessionId
  },
  /** 取走待打开会话（取一次即清空，避免重复打开） */
  consumePendingSession(): string {
    const sessionId = state.pendingSessionId
    state.pendingSessionId = ''
    return sessionId
  }
}

export const useWorkbenchBridge = () => workbenchBridge
